import React, { useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Avatar,
  Card,
  Stack,
  Divider,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CallIcon from '@mui/icons-material/Call';
import { useAuth } from '../context/AuthContext';
import { useTraining } from '../context/TrainingContext';
import { useAppTheme } from '../context/ThemeContext';

const Profile = () => {

  const { user } = useAuth();
  const { mode } = useAppTheme();
  const { modules, getTrainingModules, overallProgress } = useTraining();
  const overall = overallProgress();

  useEffect(() => {
    getTrainingModules();
  }, [getTrainingModules]);

  const profileData = modules?.id ? modules : user;

  const Field = ({ label, value }) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Typography
        variant="body1"
        fontWeight={500}
        sx={{
          pb: 1,
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700}>
        Welcome, {profileData?.name || user?.name}!
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Track your training progress and complete all modules.
      </Typography>

      <Grid container spacing={3}>
        {/* Overall Progress Banner */}
        <Grid size={{xs: 12}}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 2,
              borderRadius: 2,
              border: `1px solid ${mode === 'dark' ? "#fff" : "rgba(0,0,0,0.06)"} `,
              background: "linear-gradient(135deg, #1a237e08, #00897b08)",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#1a237e",
                width: 56,
                height: 56,
                alignSelf: { xs: "center", sm: "auto" },
              }}
            >
              <AssessmentIcon />
            </Avatar>

            <Box
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                Progress Status
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Your completion of Video
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, width: "100%" }}>
              <LinearProgress
                variant="determinate"
                value={overall}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  bgcolor: mode === "dark" ? "rgba(26, 34, 126, 0.85)" : "rgba(26,35,126,0.1)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 6,
                    background: "linear-gradient(90deg, #1a237e, #17e4cfff)",
                  },
                }}
              />
            </Box>

            <Typography
              variant="h5"
              fontWeight={700}
              color="#1a237e"
              sx={{ textAlign: "center" }}
            >
              {overall}%
            </Typography>
          </Paper>
        </Grid>       

        <Grid size={{xs: 12}}>

            <Card
              elevation={6}
              sx={{
                width: "100%",        
                borderRadius: 2,
                border: "1px solid #fff",
                overflow: "hidden",
              }}
            >

              <Grid container>
                {/* Left Section */}
                <Grid
                  size={{ xs: 12, md: 3 }}
                  sx={{
                    bgcolor: mode === 'dark' ? "#1b1b1bff" :"grey.100",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRight: { md: "1px solid #e0e0e0" },
                    p: 4,
                  }}
                >
                  <Stack spacing={2} alignItems="center">
                    <Avatar
                      src={profileData?.profile_image || user?.profile_image}
                      sx={{
                        width: 160,
                        height: 160,
                        border: "4px solid",
                        borderColor: "primary.main",
                      }}
                    />

                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      {profileData?.name || user?.name || "Unknown User"}
                    </Typography>
                    {(profileData?.phone_number || user?.phone_number) && (
                    <Chip
                      icon={<CallIcon/>}
                      label={profileData?.phone_number || user?.phone_number || ""}
                      color="primary"
                      variant="outlined"
                    />
                    )}
                  </Stack>
                </Grid>

                {/* Right Section */}
                <Grid size={{ xs: 12, md: 9 }}>
                  <Box sx={{p: 2}}>
                    <Typography variant="h6" fontWeight={700} color="primary" mb={2}>
                      Personal Details
                    </Typography>
                    
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Field label="Date of Birth" value={profileData?.dob} />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Field label="Gender" value={profileData?.gender?.toUpperCase()} />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Field label="Father's Name" value={profileData?.father_name} />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Field label="Reference Code" value={profileData?.reference_code} />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Field label="Email" value={profileData?.email} />
                      </Grid>        

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Field label="Country" value={profileData?.country} />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Field label="State" value={profileData?.state} />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Field label="City" value={profileData?.city} />
                      </Grid>

                      <Grid size={12}>
                        <Field label="Address" value={profileData?.address} />
                      </Grid>
                    </Grid>

                    {profileData?.recruited_by && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" fontWeight={700} color="primary" mb={2}>
                          Recruitment Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Field label="Recruited By" value={profileData.recruited_by.full_name} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Field label="Recruiter Phone" value={profileData.recruited_by.contact_number} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Field label="Recruiter Email" value={profileData.recruited_by.email} />
                          </Grid>
                          {profileData.recruited_by.team_leader?.full_name && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Field label="Team Leader" value={profileData.recruited_by.team_leader.full_name} />
                            </Grid>
                          )}
                        </Grid>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Card>        
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
